import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { MAX_GROUP_NAME_LENGTH } from '@breakoutlearning/firebase-repository/constants'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { DateTime } from 'luxon'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState, useId, useRef } from 'react'
import classNames from 'classnames'
import { BreakoutDateTimeInput } from 'components/design-system/BreakoutDateTimeInput'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import type { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { type RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { dateSchema } from 'util/schema-date'
import { useTranslationTyped } from 'i18n/i18n'

export const RoomStateForm = observer(function RoomStateForm({
  cubit,
  onSave,
  isCreate = false,
}: {
  cubit: StudentAssignmentCubit
  onSave: () => void
  isCreate?: boolean
}) {
  const now = DateTime.now().startOf('minute') // that allows you to use "now" when starting a meeting
  const nowFormatted = now
  const maxFormatted = cubit.effectiveExpiresAtDate

  const [saving, setSaving] = useState(false)
  const { t, tt } = useTranslationTyped()
  const headingId = useId()
  const groupNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isCreate) cubit.logEvent('show_create_group')
  }, [cubit, isCreate])

  const getSchema = useCallback(
    (
      t: ReturnType<typeof useTranslation>['t'],
      room: RoomState,
      rooms: RoomState[],
      expiresAt: DateTime | undefined
    ) =>
      z.object({
        groupName: z
          .preprocess(
            (s) => {
              if (typeof s === 'string') {
                return s.trim()
              }
              return s
            },
            z
              .string()
              .min(1, t('student_assignment.group_name_required'))
              .max(
                MAX_GROUP_NAME_LENGTH,
                t('student_assignment.group_name_too_long', {
                  maxLength: MAX_GROUP_NAME_LENGTH,
                })
              )
          )
          .refine((value) => {
            // Check if the group name is unique
            return !rooms.some(
              (r) => {
                if (r.id === room.id) return false
                if (r.data.roomStateName === value) return true
              },
              {
                message: t('student_assignment.group_name_exists'),
              }
            )
          }),
        scheduledAt: dateSchema({
          required: t('student_assignment.scheduled_date_required'),
          invalid: t('student_assignment.scheduled_date_invalid'),
          min: {
            date: now,
            message: t('student_assignment.scheduled_date_in_past'),
          },
          max: expiresAt
            ? {
                date: expiresAt,
                message: t(
                  'student_assignment.scheduled_date_after_deadline'
                ).replace(
                  '${deadline}',
                  expiresAt.toLocaleString(DateTime.DATETIME_SHORT)
                ),
              }
            : undefined,
        }),
      }),
    [now]
  )

  const schema = useMemo(
    () =>
      getSchema(
        t,
        cubit.roomState,
        cubit.roomStates.models,
        cubit.effectiveExpiresAtDate
      ),
    [
      getSchema,
      t,
      cubit.roomState,
      cubit.roomStates,
      cubit.effectiveExpiresAtDate,
    ]
  )
  type FormValues = z.infer<ReturnType<typeof getSchema>>

  const {
    control,
    setValue,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      groupName: cubit.roomState.data.roomStateName || '',
      // libraryObject.scheduledAtDate is undefined for an incomplete
      // (mustScheduleSuspended) session, so we fall back to a sensible future
      // default instead of pre-filling the stale past date. See BL-4399.
      scheduledAt: cubit.libraryObject.scheduledAtDate || now,
    },
  })

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      handleSubmit(async (data) => {
        const { groupName, scheduledAt } = data
        setSaving(true)
        try {
          await cubit.updateRoom(groupName, scheduledAt)
          onSave?.()
        } catch (e) {
          console.error(e)
        } finally {
          setSaving(false)
        }
      })(e)
    },
    [cubit, handleSubmit, onSave]
  )

  // sync scheduledAt from libraryObject (undefined for incomplete sessions,
  // so we don't overwrite the future default with a stale past date). BL-4399.
  useEffect(() => {
    return reaction(
      () => cubit.libraryObject.scheduledAtDate,
      (scheduledAtDate) => {
        if (!scheduledAtDate) return
        setValue('scheduledAt', scheduledAtDate)
      },
      { fireImmediately: true }
    )
  }, [cubit, setValue])

  return (
    <Dialog
      size="sm"
      data-testid="room-state-form-dialog"
      className="rounded-2xl"
      innerClassName="flex flex-col justify-center h-full"
      ignoreBackdropClick
      aria-labelledby={headingId}
      initialFocus={groupNameInputRef}
    >
      <DialogCloseButton />
      <div className="h-full w-full text-core-on-tertiary">
        <form className="flex h-full flex-col" onSubmit={onSubmit}>
          <h1 id={headingId} className="mb-5 text-headline-large">
            {t('student_assignment.group_details')}
          </h1>
          <div className="flex flex-col gap-y-4">
            <Controller
              control={control}
              name="groupName"
              render={({ field: { ref: fieldRef, ...field }, fieldState }) => (
                <BreakoutTextInput
                  {...field}
                  ref={(element) => {
                    fieldRef(element)
                    groupNameInputRef.current = element
                  }}
                  kind="secondary"
                  error={fieldState.error}
                  testId="room-state-form-name"
                  label={t('student_assignment.group_name')}
                  name="groupName"
                  placeholder={t('student_assignment.group_name_placeholder')}
                />
              )}
            />
            <Controller
              control={control}
              name="scheduledAt"
              render={({ field, fieldState }) => (
                <BreakoutDateTimeInput
                  {...field}
                  error={fieldState.error}
                  value={field.value}
                  kind="secondary"
                  label={tt.student_assignment.meeting_time()}
                  min={nowFormatted}
                  max={maxFormatted}
                  id="meeting-time"
                  name="scheduledAt"
                  onChange={field.onChange}
                  resolution="15min"
                />
              )}
            />
          </div>
          <div className="mt-2 px-4 text-label-small">
            {t('student_assignment.scheduled_time_info')}
          </div>
          <div className="mt-auto">
            <BreakoutButton
              data-testid="room-state-form-confirm"
              size="large"
              loading={isSubmitting}
              type="submit"
              className={classNames(
                'mr-4 w-full bg-core-primary text-core-on-primary',
                saving ? 'cursor-not-allowed opacity-50' : ''
              )}
            >
              {saving ? 'Saving...' : 'Confirm'}
            </BreakoutButton>
          </div>
        </form>
      </div>
    </Dialog>
  )
})
